import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectAuthLoading, selectAuthError } from '../store/slices/authSlice';

const VerifyContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

const LogoSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30%;
  background-color: #1A1625;
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    padding-top: 60px;
    padding-bottom: 30px;
  }
`;

const ContentSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 70%;
  padding: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  }
`;

const VerifyCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 60px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  align-self: flex-start;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.lg};
  }
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.xl};
  }
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 16px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const CodeInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const CodeInput = styled.input`
  width: 50px;
  height: 50px;
  background-color: ${({ theme }) => theme.colors.background.card};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  text-align: center;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: #9333EA;
  }
  
  /* Hide spinner for number inputs */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  /* Firefox */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const Button = styled.button`
  background-color: #9333EA;
  color: white;
  border: none;
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.action.hover};
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: ${({ theme }) => theme.spacing.md};
    padding: 14px;
  }
`;

const ResendText = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  text-align: center;
  
  span {
    color: #9333EA;
    cursor: pointer;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff5252;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

// Mobile-specific components
const NumPad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xxl};
  width: 100%;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const NumKey = styled.button`
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 24px;
  font-weight: 500;
  height: 60px;
  border-radius: 4px;
  cursor: pointer;
  
  &:active {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Verify: React.FC = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const loading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  
  // Get phone number from location state or use a default
  const phoneNumber = location.state?.phoneNumber || '+1 000 xxx x123';
  
  // Focus the first input on component mount
  useEffect(() => {
    const firstInput = inputRefs.current[0];
    if (firstInput) {
      firstInput.focus();
    }
  }, []);
  
  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d+$/.test(value)) return;
    
    // Update the code array
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    
    // Auto-focus next input if value is entered
    if (value && index < 5) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };
  
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Move to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
      }
    }
  };
  
  const handleNumPadClick = (value: string) => {
    // Find the first empty input or the last one
    const index = code.findIndex(digit => !digit);
    const targetIndex = index === -1 ? 5 : index;
    
    if (value === 'backspace') {
      // Handle backspace
      if (targetIndex > 0 || (targetIndex === 0 && code[0])) {
        const newCode = [...code];
        const deleteIndex = code[targetIndex] ? targetIndex : targetIndex - 1;
        newCode[deleteIndex] = '';
        setCode(newCode);
        
        const inputToFocus = inputRefs.current[deleteIndex];
        if (inputToFocus) {
          inputToFocus.focus();
        }
      }
    } else {
      // Handle number input
      handleCodeChange(targetIndex, value);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if code is complete
    if (code.some(digit => !digit)) {
      setFormError('Please enter the complete verification code');
      return;
    }
    
    setFormError('');
    setIsLoading(true);
    
    // TODO: Implement verification logic with API
    // For now, simulate API call with timeout
    setTimeout(() => {
      setIsLoading(false);
      navigate('/invitation', { 
        state: { phoneNumber: location.state?.phoneNumber } 
      });
    }, 1000);
  };
  
  const handleResendCode = () => {
    // TODO: Implement resend code functionality
    alert('Code resent successfully!');
  };
  
  const handleBack = () => {
    // Navigate back to signup page
    navigate('/signup');
  };
  
  return (
    <VerifyContainer>
      <LogoSide>
        <Logo>
          <img src="/creator-logo.svg" alt="BlinkLink Creator" />
        </Logo>
      </LogoSide>
      <ContentSide>
        <VerifyCard>
          <BackButton onClick={handleBack}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            One Time Verification
          </BackButton>
          
          <Subtitle>Enter the six digit code sent to {phoneNumber}</Subtitle>
          
          <form onSubmit={handleSubmit}>
            <CodeInputContainer>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <CodeInput
                  key={index}
                  ref={(el: HTMLInputElement | null) => {
                    inputRefs.current[index] = el;
                  }}
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={code[index]}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                />
              ))}
            </CodeInputContainer>
            
            {formError && <ErrorMessage>{formError}</ErrorMessage>}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
            
            <ResendText>
              Didn't receive code? <span onClick={handleResendCode}>Send Again</span>
            </ResendText>
          </form>
          
          <NumPad>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0].map((num) => (
              <NumKey key={num} onClick={() => handleNumPadClick(num.toString())}>
                {num}
              </NumKey>
            ))}
            <NumKey onClick={() => handleNumPadClick('backspace')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                <line x1="18" y1="9" x2="12" y2="15" />
                <line x1="12" y1="9" x2="18" y2="15" />
              </svg>
            </NumKey>
          </NumPad>
        </VerifyCard>
      </ContentSide>
    </VerifyContainer>
  );
};

export default Verify;
